import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  PhoneCall, Sparkles, Loader2, Save, Play, Pause, Clock, UserPlus,
  ShoppingBag, PlugZap, History, Check, X,
} from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";

const OBJECTIVES = [
  { id: "recruit", label: "Reclutar nuevos distribuidores", desc: "La IA invita a las personas a unirse a tu equipo.", icon: UserPlus },
  { id: "sell", label: "Vender productos", desc: "La IA presenta y ofrece tus productos.", icon: ShoppingBag },
];

const DURATIONS = [2, 3, 5];

const RESULT_LABEL = {
  interested: { text: "Interesado", cls: "bg-green-500/15 text-green-300 border-green-500/30" },
  not_interested: { text: "No interesado", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  no_answer: { text: "No contestó", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
  voicemail: { text: "Buzón de voz", cls: "bg-yellow-400/15 text-yellow-impact border-yellow-400/30" },
};

const CallRow = ({ c }) => {
  const r = RESULT_LABEL[c.result] || { text: c.result || "—", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" };
  return (
    <div className="rounded-xl border border-white/10 bg-[#121212] p-4 flex flex-wrap items-center gap-3">
      <PhoneCall size={16} className="text-purple-400 shrink-0" />
      <div className="flex-1 min-w-[140px]">
        <p className="text-sm font-semibold text-zinc-200">{c.to_phone || c.lead_name || "Prospecto"}</p>
        <p className="text-xs text-zinc-500">{(c.created_at || "").slice(0, 16).replace("T", " ")} · {c.duration_sec ? `${Math.round(c.duration_sec / 60)} min` : "—"}</p>
      </div>
      <span className={`text-xs border rounded-full px-2.5 py-1 font-semibold ${r.cls}`}>{r.text}</span>
    </div>
  );
};

export default function AiCalls() {
  const [settings, setSettings] = useState(null);
  const [objective, setObjective] = useState("recruit");
  const [kb, setKb] = useState("");
  const [duration, setDuration] = useState(3);
  const [status, setStatus] = useState("paused");
  const [optimized, setOptimized] = useState("");
  const [optimizing, setOptimizing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [calls, setCalls] = useState([]);

  useEffect(() => {
    api.get("/calls/settings").then((r) => {
      setSettings(r.data);
      setObjective(r.data.objective || "recruit");
      setKb(r.data.knowledge_base || "");
      setDuration(r.data.max_duration_min || 3);
      setStatus(r.data.status || "paused");
    }).catch(() => {});
    api.get("/calls/history").then((r) => setCalls(r.data.calls || [])).catch(() => {});
  }, []);

  const optimize = async () => {
    setOptimizing(true);
    try {
      const { data } = await api.post("/calls/optimize-knowledge", { text: kb, objective });
      setOptimized(data.optimized);
      toast.success("¡Información optimizada! Revísala y aplícala si te gusta.");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setOptimizing(false);
  };

  const save = async (nextStatus) => {
    setSaving(true);
    try {
      const st = nextStatus || status;
      const { data } = await api.put("/calls/settings", {
        objective, knowledge_base: kb, max_duration_min: duration, status: st,
      });
      setStatus(data.status);
      if (nextStatus === "active" && data.status === "paused" && !data.twilio_connected) {
        toast.info("Configuración guardada. El sistema se activará en cuanto se conecte el número de Twilio.");
      } else {
        toast.success("Cambios guardados");
      }
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setSaving(false);
  };

  if (!settings) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-purple-500" size={28} /></div>;

  if (!settings.plan_active) {
    return (
      <div data-testid="calls-upsell" className="max-w-xl mx-auto text-center py-16">
        <PhoneCall className="mx-auto text-purple-400 mb-4" size={40} />
        <h1 className="font-display font-black text-3xl mb-3">Llamadas Automáticas con IA</h1>
        <p className="text-zinc-400 mb-6">Tu asistente de voz llama a tus prospectos, les presenta tu oportunidad o productos y te avisa quiénes están interesados. Disponible con el paquete Élite.</p>
        <Link to="/dashboard/planes"><Button className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl px-6 py-5">Ver paquete Élite</Button></Link>
      </div>
    );
  }

  return (
    <div data-testid="ai-calls-page" className="max-w-3xl">
      <div className="flex items-center gap-3 mb-2">
        <PhoneCall className="text-purple-400" size={26} />
        <h1 className="font-display font-black tracking-tight text-3xl">Llamadas Automáticas con IA</h1>
      </div>
      <p className="text-zinc-400 mb-6">Configura a tu asistente de voz: qué ofrece, cómo lo dice y cuánto dura cada llamada.</p>

      {!settings.twilio_connected && (
        <div data-testid="twilio-banner" className="rounded-2xl border border-yellow-400/30 bg-yellow-400/5 p-4 mb-6 flex items-start gap-3">
          <PlugZap size={18} className="text-yellow-impact mt-0.5 shrink-0" />
          <p className="text-sm text-zinc-300">
            <strong className="text-yellow-impact">El número telefónico aún no está conectado.</strong> Puedes dejar todo configurado desde ahora;
            en cuanto se conecte el número (Twilio), tus llamadas se encenderán con esta configuración.
          </p>
        </div>
      )}

      {/* 1. Objetivo */}
      <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 mb-5">
        <h2 className="font-display font-bold text-lg mb-4">1. ¿Cuál es el objetivo de tus llamadas?</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {OBJECTIVES.map((o) => (
            <button
              key={o.id}
              type="button"
              data-testid={`call-objective-${o.id}`}
              onClick={() => setObjective(o.id)}
              className={`text-left rounded-xl border p-5 transition-all ${objective === o.id ? "border-purple-500 bg-purple-600/10" : "border-white/15 bg-black hover:border-purple-500/50"}`}
            >
              <o.icon size={22} className={objective === o.id ? "text-yellow-400" : "text-purple-400"} />
              <p className="font-semibold text-sm mt-2 text-zinc-100">{o.label}</p>
              <p className="text-xs text-zinc-500 mt-1">{o.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* 2. Base de conocimiento */}
      <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 mb-5">
        <h2 className="font-display font-bold text-lg mb-1">2. Información para tus llamadas</h2>
        <p className="text-xs text-zinc-500 mb-4">Escribe con tus palabras los detalles de tu producto, empresa o plan de compensación. La IA usará esta información al hablar con tus prospectos.</p>
        <Label className="text-zinc-300">Tu información</Label>
        <Textarea
          data-testid="call-knowledge"
          value={kb}
          onChange={(e) => setKb(e.target.value)}
          rows={8}
          placeholder="Ej. Vendemos suplementos naturales de la empresa X. Beneficios: más energía, control de peso... Nuestro plan de compensación paga bonos por..."
          className="mt-2 bg-black border-white/15 rounded-xl focus-visible:ring-purple-500"
        />
        <Button
          data-testid="optimize-knowledge"
          onClick={optimize}
          disabled={optimizing || kb.trim().length < 20}
          className="mt-3 bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 text-white font-bold rounded-xl px-5"
        >
          {optimizing ? <Loader2 className="animate-spin mr-2" size={16} /> : <Sparkles size={16} className="mr-2" />} Optimizar Información
        </Button>
        {optimized && (
          <div data-testid="optimized-result" className="mt-4 rounded-xl border border-purple-500/40 bg-purple-600/5 p-4">
            <p className="text-xs font-bold text-purple-300 mb-2">✨ Versión optimizada para llamadas:</p>
            <p className="text-sm text-zinc-200 whitespace-pre-wrap">{optimized}</p>
            <div className="flex gap-2 mt-4">
              <Button data-testid="apply-optimized" onClick={() => { setKb(optimized); setOptimized(""); toast.success("Versión optimizada aplicada. No olvides guardar."); }} className="bg-green-600 hover:bg-green-500 rounded-xl">
                <Check size={15} className="mr-2" /> Usar esta versión
              </Button>
              <Button onClick={() => setOptimized("")} variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 rounded-xl">
                <X size={15} className="mr-2" /> Descartar
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* 3. Duración */}
      <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 mb-5">
        <h2 className="font-display font-bold text-lg mb-1">3. Duración máxima de cada llamada</h2>
        <p className="text-xs text-zinc-500 mb-4">Limita la duración para controlar costos y mantener el interés del prospecto. Al llegar al límite, la IA cierra la llamada con amabilidad.</p>
        <div className="flex gap-3">
          {DURATIONS.map((d) => (
            <button
              key={d}
              type="button"
              data-testid={`call-duration-${d}`}
              onClick={() => setDuration(d)}
              className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-bold transition-all ${duration === d ? "border-yellow-400 bg-yellow-400 text-black" : "border-white/15 bg-black text-zinc-300 hover:border-yellow-400/50"}`}
            >
              <Clock size={15} /> {d} minutos
            </button>
          ))}
        </div>
      </div>

      {/* 4. Estado y guardar */}
      <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 mb-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span data-testid="calls-status" className={`inline-flex items-center gap-1.5 text-xs font-bold border rounded-full px-3 py-1.5 ${status === "active" ? "border-green-500/40 bg-green-500/10 text-green-300" : "border-yellow-400/40 bg-yellow-400/10 text-yellow-impact"}`}>
              {status === "active" ? <Play size={12} /> : <Pause size={12} />} {status === "active" ? "Sistema ACTIVO" : "Sistema PAUSADO"}
            </span>
            <button
              data-testid="toggle-status"
              onClick={() => save(status === "active" ? "paused" : "active")}
              disabled={saving}
              className="text-xs text-purple-300 underline hover:text-purple-200"
            >
              {status === "active" ? "Pausar" : "Activar"}
            </button>
          </div>
          <Button data-testid="save-call-settings" onClick={() => save()} disabled={saving} className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl px-6 py-5">
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />} Guardar cambios
          </Button>
        </div>
      </div>

      {/* 5. Historial */}
      <div className="mb-4 flex items-center gap-2">
        <History size={18} className="text-purple-400" />
        <h2 className="font-display font-bold text-lg">Historial de llamadas</h2>
      </div>
      {calls.length === 0 ? (
        <div data-testid="calls-empty" className="rounded-2xl border border-dashed border-white/15 bg-black/30 p-8 text-center">
          <PhoneCall className="mx-auto text-zinc-600 mb-3" size={26} />
          <p className="text-sm text-zinc-400">Aún no hay llamadas registradas.</p>
          <p className="text-xs text-zinc-500 mt-1">{settings.twilio_connected ? "Cuando el sistema haga llamadas, aquí verás la transcripción y el resultado de cada una." : "En cuanto se conecte el número telefónico, aquí verás cada llamada con su resultado."}</p>
        </div>
      ) : (
        <div className="space-y-3">{calls.map((c) => <CallRow key={c.call_id} c={c} />)}</div>
      )}
    </div>
  );
}
